





































































import Vue from "vue";
import { mapActions } from "vuex";
import { PaymentStatus } from "@/shared/types/payment-status/PaymentStatus.type";
import CountdownButton from "@/shared/components/CountdownButton.vue";

export default Vue.extend({
    components: {
        "countdown-button": CountdownButton
    },
    data: function() {
        return {
            status: "PROCESSING" as PaymentStatus,
            statusChecker: null as ReturnType<typeof setTimeout> | null,
            errorCount: 0,
            maxErrors: 3,
            monitoringStarted: false,
        };
    },

    computed: {
        classifiedId: function(): string {
            return (this.$route.params.id as string) || "";
        },
        pricingType: function(): string {
            return (this.$route.query.action as string) || "PUBLICATION";
        },
    },

    mounted: function(): void {
        const hostedCheckoutId: string | null =
            (this.$route.query.hostedCheckoutId as string) ||
            localStorage.getItem("hostedCheckoutId");

        if (!hostedCheckoutId) {
            this.status = "ERROR";
            return;
        }

        if (this.monitoringStarted) {
            return;
        }
        this.monitoringStarted = true;

        localStorage.setItem("hostedCheckoutId", hostedCheckoutId);

        this.cleanUrlParams();
        this.monitorPaymentStatus(hostedCheckoutId);
    },

    beforeDestroy: function(): void {
        if (this.statusChecker) {
            clearTimeout(this.statusChecker);
            this.statusChecker = null;
        }
        this.monitoringStarted = false;
    },

    methods: {
        ...mapActions("OrdersModule", ["getTransactionStatus"]),

        cleanUrlParams: function(): void {
            const url = new URL(window.location.href);
            const paramsToRemove = ["RETURNMAC", "hostedCheckoutId"];
            let changed = false;

            paramsToRemove.forEach((param) => {
                if (url.searchParams.has(param)) {
                    url.searchParams.delete(param);
                    changed = true;
                }
            });

            if (changed) {
                window.history.replaceState({}, "", url.pathname + url.hash);
            }
        },

        monitorPaymentStatus: function(hostedCheckoutId: string): void {
            if (this.statusChecker) {
                return;
            }
            this.errorCount = 0;

            const poll = async (): Promise<void> => {
                try {
                    const apiStatus: string = await (this as any).getTransactionStatus(
                        hostedCheckoutId
                    );
                    this.status = this.mapStatus(apiStatus);
                    this.errorCount = 0;

                    if (this.status === "COMPLETE") {
                        this.statusChecker = null;
                        setTimeout(() => { this.redirectToConfirmation(); }, 1500);
                        return;
                    } else if (this.status === "FAILED") {
                        this.statusChecker = null;
                        return;
                    }
                } catch {
                    this.errorCount++;
                    if (this.errorCount >= this.maxErrors) {
                        this.status = "ERROR";
                        this.statusChecker = null;
                        return;
                    }
                }

                this.statusChecker = setTimeout(poll, 3000);
            };

            poll();
        },

        redirectToConfirmation: function(): void {
            const isOptimized = localStorage.getItem("optimized") === "true";

            const routeResolved = this.$router.resolve({
                name: "confirmation",
                params: { id: this.classifiedId },
                query: { 
                    success: "true", 
                    action: this.pricingType,
                    optimized: String(isOptimized)
                },
            });

            const baseUrl = process.env.VUE_APP_PAYMENT_REDIRECT_URL;

            // Ensure /classifieds is included only if not already present
            let href = routeResolved.href.startsWith("/classifieds") ? routeResolved.href : `/classifieds${routeResolved.href}`;

            // Replace '#' with 'index.html#' for payment redirect
            href = href.replace("#", "index.html#");

            const fullUrl = `${baseUrl}${href}`;
            window.location.replace(fullUrl);
            localStorage.removeItem("optimized");
        },

        mapStatus: function(apiStatus: string): PaymentStatus {
            const statusMap: Record<string, PaymentStatus> = {
                COMPLETE: "COMPLETE",
                FAILED: "FAILED",
                INITIATED: "PROCESSING",
            };
            return statusMap[apiStatus] || "ERROR";
        },

        resolveBackRouteKey: function(isOptimized: boolean): string {
            switch (this.pricingType) {
            case "UPGRADE":
                return "visibility-upgrade-payment";
            case "EXTENSION":
            case "RENEWAL":
                return "extend-payment";
            default:
                return isOptimized ? "route-optimized-encoding" : "step-payment";
            }
        },

        goBackToFunnel: function(): void {
            const isOptimized = localStorage.getItem("optimized") === "true";
            const routeKey = this.resolveBackRouteKey(isOptimized);
            const localizedPath = this.$t(routeKey) as string;
            const separator = localizedPath.endsWith("/") ? "" : "/";
            const target = `${localizedPath}${separator}${this.classifiedId}`;

            localStorage.removeItem("hostedCheckoutId");
            localStorage.removeItem("optimized");

            this.$router.replace(target);
        },
    },
});
